<div class="workspace" *ngIf="representativeSiteDetail">
  <!-- ===================================================================== -->
  <!-- SHARED HEADER (rendered once) — Back · address · rollup status pill    -->
  <!-- + per-status counts. Per-product status pill + stepper live inside     -->
  <!-- each product card below.                                               -->
  <!-- ===================================================================== -->
  <header class="workspace__header">
    <!-- history-back (not a routerLink) so this is a POP navigation — the
         app-wide scroll-restoration config only restores scroll on POP. -->
    <button
      type="button"
      class="btn btn--secondary workspace__back"
      (click)="back()"
      data-testid="workspace-back"
    >
      <span class="material-icons">arrow_back</span>{{ backLabel }}
    </button>

    <div class="workspace__title-row">
      <div class="workspace__title-block">
        <h1 class="workspace__title">
          {{ address.streetAddress }}<br />{{ address.cityStateZip }}
        </h1>
        <p class="workspace__service-type" data-testid="workspace-site-summary">
          <span *ngIf="siteNumber != null">Site #{{ siteNumber }} · </span
          >{{ products.length }}
          {{ products.length === 1 ? "product" : "products" }}
        </p>
      </div>

      <div class="workspace__rollup">
        <app-status-pill
          [status]="rollupPillKey"
          [label]="rollupPillLabel"
          testid="workspace-rollup-status"
        ></app-status-pill>
        <div class="workspace__rollup-counts" data-testid="workspace-status-counts">
          <span
            class="workspace__count workspace__count--danger"
            *ngIf="counts.pastDue"
            data-testid="workspace-count-past-due"
            >{{ counts.pastDue }} Past Due</span
          >
          <span
            class="workspace__count workspace__count--warning"
            *ngIf="counts.revisionRequested"
            data-testid="workspace-count-revision-requested"
            >{{ counts.revisionRequested }} Revision Requested</span
          >
          <span
            class="workspace__count workspace__count--warning"
            *ngIf="counts.inReview"
            data-testid="workspace-count-in-review"
            >{{ counts.inReview }} In Review</span
          >
          <span
            class="workspace__count workspace__count--info"
            *ngIf="counts.active"
            data-testid="workspace-count-active"
            >{{ counts.active }} Active</span
          >
          <span
            class="workspace__count workspace__count--success"
            *ngIf="counts.complete"
            data-testid="workspace-count-complete"
            >{{ counts.complete }} Complete</span
          >
        </div>
      </div>
    </div>
  </header>

  <!-- ===================================================================== -->
  <!-- SHARED SITE INFO (rendered once) — parcel map + geometry, PM, Table A  -->
  <!-- + changes, Special Site Condition, Site Details, Site-Visit            -->
  <!-- Instructions. Site-level fields, identical across the site's products. -->
  <!-- ===================================================================== -->
  <div class="workspace__grid">
    <section class="card workspace__map-card">
      <p class="workspace__section-label">Site Map</p>
      <google-map
        *ngIf="mapsLoaded"
        height="360px"
        width="100%"
        [center]="center"
        [zoom]="zoom"
        [options]="mapOptions"
      >
        <map-polygon
          [paths]="polygonPaths"
          [options]="polygonOptions"
        ></map-polygon>
      </google-map>

      <dl class="workspace__dl workspace__site-facts">
        <div class="workspace__dl-row">
          <dt class="workspace__dl-label">Project Manager</dt>
          <dd class="workspace__dl-value">
            <a
              *ngIf="projectManager.email; else pmPlain"
              class="workspace__link"
              [href]="'mailto:' + projectManager.email"
              data-testid="workspace-pm-contact-link"
              >{{ projectManager.name }}</a
            >
            <ng-template #pmPlain>{{
              projectManager.name || "Not assigned"
            }}</ng-template>
          </dd>
        </div>
        <div class="workspace__dl-row" *ngIf="siteAcreage != null">
          <dt class="workspace__dl-label">Acreage</dt>
          <dd class="workspace__dl-value">{{ siteAcreage }} acres</dd>
        </div>
      </dl>
    </section>

    <section class="card workspace__site-info" data-testid="workspace-site-info">
      <p class="workspace__section-label">Site Details</p>

      <div
        class="workspace__callout workspace__callout--info"
        *ngIf="tableAText || tableAItemText || hasTableAChanges"
        data-testid="workspace-callout-table-a"
      >
        <span class="material-icons">checklist</span>
        <div>
          <p class="workspace__callout-title">Table A</p>
          <p class="workspace__callout-body" *ngIf="tableAText">
            {{ tableAText }}
          </p>
          <!--
            The Item 20/21 free text, on its own line inside the same callout:
            it is a Table A item's value, and the PA reading this callout is the
            person who has to act on what the client typed there.
          -->
          <p
            class="workspace__callout-body workspace__table-a-item-text"
            *ngIf="tableAItemText"
            data-testid="workspace-table-a-item-text"
          >
            <span class="workspace__table-a-item-label"
              >{{ tableAItemLabel || "Item 20/21" }}:</span
            >
            {{ tableAItemText }}
          </p>
          <p class="workspace__callout-body" *ngIf="hasTableAChanges">
            <span class="workspace__table-a-added" *ngIf="tableAItemsAdded"
              >+ {{ tableAItemsAdded }}</span
            >
            <span class="workspace__table-a-removed" *ngIf="tableAItemsRemoved"
              >&nbsp;&minus; {{ tableAItemsRemoved }}</span
            >
          </p>
        </div>
      </div>

      <div
        class="workspace__callout workspace__callout--info"
        *ngIf="siteDetailsText"
        data-testid="workspace-callout-site-details"
      >
        <span class="material-icons">info</span>
        <div>
          <p class="workspace__callout-title">Site Details</p>
          <p class="workspace__callout-body">{{ siteDetailsText }}</p>
        </div>
      </div>

      <div
        class="workspace__callout workspace__callout--warning"
        *ngIf="siteVisitInstructions"
        data-testid="workspace-callout-site-visit"
      >
        <span class="material-icons">event_note</span>
        <div>
          <p class="workspace__callout-title">Site-Visit Instructions</p>
          <p class="workspace__callout-body">{{ siteVisitInstructions }}</p>
        </div>
      </div>

      <div
        class="workspace__callout workspace__callout--danger"
        *ngIf="specialSiteCondition"
        data-testid="workspace-callout-special-condition"
      >
        <span class="material-icons">warning</span>
        <div>
          <p class="workspace__callout-title">Special Site Condition</p>
          <p class="workspace__callout-body">{{ specialSiteCondition }}</p>
        </div>
      </div>

      <p
        class="workspace__placeholder-text"
        *ngIf="
          !tableAText &&
          !tableAItemText &&
          !hasTableAChanges &&
          !siteDetailsText &&
          !siteVisitInstructions &&
          !specialSiteCondition
        "
      >
        No additional site details provided.
      </p>
    </section>
  </div>

  <!-- ===================================================================== -->
  <!-- SHARED DOCUMENTS (rendered once) — site/received documents (SITE, ATP, -->
  <!-- Project & Review) are identical across the site's products (Decision   -->
  <!-- #5), so they render ONCE here rather than duplicating in every product -->
  <!-- card. Each product card below renders only ITS OWN deliverables via a  -->
  <!-- separate `scope="product"` instance.                                  -->
  <!-- ===================================================================== -->
  <section class="card" data-testid="workspace-shared-documents">
    <p class="workspace__section-label">Project Documents</p>
    <app-documents-card
      [siteDetail]="representativeSiteDetail"
      [documents]="sharedDocuments"
      scope="shared"
      [readOnly]="readOnly"
    ></app-documents-card>
  </section>

  <!-- ===================================================================== -->
  <!-- SHARED SITE VISIT (rendered once) — one physical site = ONE site visit  -->
  <!-- (Decision #4). The card reads the site-contact negotiation's own state  -->
  <!-- (`SiteVisitSchedule`) rather than the legacy per-product preferred      -->
  <!-- windows, so it shows the same status and day the Scheduling page does.  -->
  <!-- It owns that read itself and needs only the two ids to scope it: this   -->
  <!-- workspace's project and physical site.                                  -->
  <!-- ===================================================================== -->
  <section class="card" data-testid="workspace-site-visit">
    <p class="workspace__section-label">Site Visit</p>
    <app-scheduling-card
      [products]="products"
      [projectId]="projectId"
      [siteNumber]="siteNumber"
      [readOnly]="readOnly"
    ></app-scheduling-card>
  </section>

  <!-- ===================================================================== -->
  <!-- SURVEY SECTION (0–1 product) — rendered only when non-empty.           -->
  <!-- ===================================================================== -->
  <ng-container *ngIf="surveyProducts.length">
    <div class="workspace__section-head" data-testid="workspace-section-survey">
      <span class="workspace__section-accent"></span>
      <h2 class="workspace__section-title">Survey</h2>
      <span class="workspace__section-rule"></span>
    </div>
    <div class="workspace__product-stack">
      <ng-container *ngFor="let product of surveyProducts">
        <ng-container
          *ngTemplateOutlet="productCard; context: { $implicit: product }"
        ></ng-container>
      </ng-container>
    </div>
  </ng-container>

  <!-- ===================================================================== -->
  <!-- REPORTS & ASSESSMENTS SECTION (0–N products) — rendered only when      -->
  <!-- non-empty.                                                             -->
  <!-- ===================================================================== -->
  <ng-container *ngIf="reportProducts.length">
    <div class="workspace__section-head" data-testid="workspace-section-reports">
      <span class="workspace__section-accent"></span>
      <h2 class="workspace__section-title">Reports &amp; Assessments</h2>
      <span class="workspace__section-rule"></span>
    </div>
    <div class="workspace__product-stack">
      <ng-container *ngFor="let product of reportProducts">
        <ng-container
          *ngTemplateOutlet="productCard; context: { $implicit: product }"
        ></ng-container>
      </ng-container>
    </div>
  </ng-container>
</div>

<!-- ======================================================================= -->
<!-- PER-PRODUCT CARD — one instance per product. Carries its own status      -->
<!-- pill + stepper, status banner, variant body (surveyor/action items for   -->
<!-- survey; assignment details + Quire for PA), deliverable submit, and its   -->
<!-- own Documents / Scheduling / Comments cards. (Task 2 refines the         -->
<!-- per-product behavior; Task 3 partitions documents; Task 5 consolidates   -->
<!-- scheduling.)                                                             -->
<!-- ======================================================================= -->
<ng-template #productCard let-product>
  <section
    class="card workspace__product"
    [id]="'workspace-product-' + product.site.id"
    [class.workspace__product--linked]="product.site.id === id"
    [attr.data-testid]="'workspace-product-' + product.testId"
  >
    <div class="workspace__product-head">
      <div class="workspace__product-head-main">
        <h3 class="workspace__product-title">{{ product.title }}</h3>
        <ol
          class="workspace__stages"
          [attr.data-testid]="'workspace-product-' + product.testId + '-stages'"
        >
          <li
            class="workspace__stage"
            [class.workspace__stage--active]="product.stageIndex === 0"
            [class.workspace__stage--complete]="product.stageIndex > 0"
          >
            Active
          </li>
          <li class="workspace__stage-sep material-icons">chevron_right</li>
          <li
            class="workspace__stage"
            [class.workspace__stage--active]="product.stageIndex === 1"
            [class.workspace__stage--complete]="product.stageIndex > 1"
          >
            Pending Review
          </li>
          <li class="workspace__stage-sep material-icons">chevron_right</li>
          <li
            class="workspace__stage"
            [class.workspace__stage--active]="product.stageIndex === 2"
            [class.workspace__stage--complete]="product.stageIndex === 2"
          >
            Order Complete
          </li>
        </ol>
      </div>
      <app-status-pill
        [status]="product.statusPillKey"
        [label]="product.statusPillLabel"
        [testid]="'workspace-product-' + product.testId + '-status'"
      ></app-status-pill>
    </div>

    <!-- Status banner (survey always; PA while the deliverable is still owed). -->
    <div
      *ngIf="product.showBanner"
      class="workspace__alta-banner workspace__alta-banner--{{ product.banner.triad }}"
      [attr.data-testid]="'workspace-product-' + product.testId + '-banner'"
    >
      <span class="material-icons">{{ product.banner.icon }}</span>
      <div>
        <p class="workspace__alta-banner-title">{{ product.banner.label }}</p>
        <p class="workspace__alta-banner-message">{{ product.banner.message }}</p>
      </div>
    </div>

    <!-- =================== SURVEY VARIANT BODY =================== -->
    <ng-container *ngIf="product.isAlta">
      <div class="workspace__product-grid">
        <div class="workspace__subcard" [attr.data-testid]="'workspace-product-' + product.testId + '-surveyor'">
          <p class="workspace__subcard-label">Surveyor Information</p>
          <dl class="workspace__dl">
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Date of Authorization</dt>
              <dd class="workspace__dl-value">
                {{ product.authorizationDate ? (product.authorizationDate | date : "MM/dd/yyyy") : "Not provided" }}
              </dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Business Days Quoted</dt>
              <dd class="workspace__dl-value">
                {{ product.businessDaysQuoted != null ? product.businessDaysQuoted + " days" : "Not provided" }}
              </dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Survey Due Date</dt>
              <dd class="workspace__dl-value">
                {{ product.surveyDueDate ? (product.surveyDueDate | date : "MM/dd/yyyy") : "Not provided" }}
              </dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Title Work Provided</dt>
              <dd class="workspace__dl-value">
                {{ product.titleWorkProvidedDate ? (product.titleWorkProvidedDate | date : "MM/dd/yyyy") : "Not provided" }}
              </dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Updated Due Date (Approved Delay)</dt>
              <dd class="workspace__dl-value">
                {{ product.approvedDelayDate ? (product.approvedDelayDate | date : "MM/dd/yyyy") : "On time" }}
              </dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Outstanding Comments</dt>
              <dd class="workspace__dl-value">{{ product.outstandingCommentsCount }}</dd>
            </div>
          </dl>
        </div>

        <div
          class="workspace__subcard"
          *ngIf="!readOnly"
          [attr.data-testid]="'workspace-product-' + product.testId + '-action-items'"
        >
          <p class="workspace__subcard-label">Action Items</p>
          <ul class="workspace__action-list">
            <!-- Field work only for products that require a site visit. -->
            <li class="workspace__action" *ngIf="product.siteVisitRequired">
              <span
                class="material-icons workspace__action-icon"
                [class.workspace__action-icon--done]="product.fieldWorkItem.done"
                [class.workspace__action-icon--outstanding]="!product.fieldWorkItem.done"
                >{{ product.fieldWorkItem.done ? "check_circle" : "cancel" }}</span
              >
              <span class="workspace__action-label">Field Work Scheduled</span>
              <span
                class="workspace__action-status"
                [class.workspace__action-status--done]="product.fieldWorkItem.done"
                [class.workspace__action-status--outstanding]="!product.fieldWorkItem.done"
                >{{ actionItemStatusText(product.fieldWorkItem) }}</span
              >
            </li>
            <li class="workspace__action">
              <span
                class="material-icons workspace__action-icon"
                [class.workspace__action-icon--done]="product.prelimSurveyItem.done"
                [class.workspace__action-icon--outstanding]="!product.prelimSurveyItem.done"
                >{{ product.prelimSurveyItem.done ? "check_circle" : "cancel" }}</span
              >
              <span class="workspace__action-label">Preliminary Survey Delivered</span>
              <span
                class="workspace__action-status"
                [class.workspace__action-status--done]="product.prelimSurveyItem.done"
                [class.workspace__action-status--outstanding]="!product.prelimSurveyItem.done"
                >{{ actionItemStatusText(product.prelimSurveyItem) }}</span
              >
            </li>
            <li class="workspace__action">
              <span
                class="material-icons workspace__action-icon"
                [class.workspace__action-icon--done]="product.revisedSurveyItem.done"
                [class.workspace__action-icon--outstanding]="!product.revisedSurveyItem.done"
                >{{ product.revisedSurveyItem.done ? "check_circle" : "cancel" }}</span
              >
              <span class="workspace__action-label">Latest Revised Survey Uploaded</span>
              <span
                class="workspace__action-status"
                [class.workspace__action-status--done]="product.revisedSurveyItem.done"
                [class.workspace__action-status--outstanding]="!product.revisedSurveyItem.done"
                >{{ actionItemStatusText(product.revisedSurveyItem) }}</span
              >
            </li>
          </ul>
        </div>
      </div>
    </ng-container>

    <!-- =================== PA VARIANT BODY =================== -->
    <ng-container *ngIf="!product.isAlta">
      <div class="workspace__product-grid">
        <div class="workspace__subcard" [attr.data-testid]="'workspace-product-' + product.testId + '-assignment'">
          <p class="workspace__subcard-label">Assignment Details</p>
          <dl class="workspace__dl">
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Due Date</dt>
              <dd class="workspace__dl-value">
                {{ product.dueDate ? (product.dueDate | date : "MM/dd/yyyy") : "—" }}
              </dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Your Committed Date</dt>
              <dd class="workspace__dl-value">
                {{ product.committedDate ? (product.committedDate | date : "MM/dd/yyyy") : "—" }}
              </dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Report ID</dt>
              <dd class="workspace__dl-value">{{ product.reportId }}</dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Staffing Coordinator</dt>
              <dd class="workspace__dl-value">
                <a
                  *ngIf="product.staffingCoordinator.email; else scPlain"
                  class="workspace__link"
                  [href]="'mailto:' + product.staffingCoordinator.email"
                  [attr.data-testid]="'workspace-product-' + product.testId + '-staffing-coordinator-link'"
                  >{{ product.staffingCoordinator.name }}</a
                >
                <ng-template #scPlain>{{
                  product.staffingCoordinator.name || "Not assigned"
                }}</ng-template>
              </dd>
            </div>
            <div class="workspace__dl-row">
              <dt class="workspace__dl-label">Senior Reviewer</dt>
              <dd class="workspace__dl-value">
                {{ product.seniorReviewer.name || "Not assigned" }}
              </dd>
            </div>
            <!-- Site-visit row only for products that require a visit. -->
            <div class="workspace__dl-row" *ngIf="product.siteVisitRequired">
              <dt class="workspace__dl-label">Site Visit</dt>
              <dd class="workspace__dl-value">{{ product.siteVisitSummary }}</dd>
            </div>
          </dl>
        </div>

        <!-- Report Writer (Quire) — non-survey products only. -->
        <div
          class="workspace__subcard"
          *ngIf="!readOnly"
          [attr.data-testid]="'workspace-product-' + product.testId + '-report-writer'"
        >
          <p class="workspace__subcard-label">Report Writer</p>
          <a
            *ngIf="product.hasReportWriterLink; else quirePending"
            class="btn btn--primary workspace__quire-btn"
            [href]="product.reportWriterUrl"
            target="_blank"
            rel="noopener"
            [attr.data-testid]="'workspace-product-' + product.testId + '-open-quire'"
          >
            Open in Quire <span class="material-icons">open_in_new</span>
          </a>
          <ng-template #quirePending>
            <button
              type="button"
              class="btn btn--primary workspace__quire-btn"
              disabled
              [attr.data-testid]="'workspace-product-' + product.testId + '-open-quire'"
            >
              Report Writer Link Pending
            </button>
          </ng-template>
        </div>
      </div>
    </ng-container>

    <!-- =================== DELIVERABLE SUBMIT (per product) =================== -->
    <div class="workspace__deliverable" *ngIf="!readOnly">
      <button
        *ngIf="product.isReviewStage"
        type="button"
        class="btn btn--primary workspace__submit-btn"
        [disabled]="!product.showReviewSubmission"
        (click)="openSubmitForReview(product)"
        [attr.data-testid]="'workspace-product-' + product.testId + '-submit-review'"
      >
        {{ product.showReviewSubmission ? "Submit Review" : "Deliverable Document Required" }}
      </button>

      <button
        *ngIf="product.isRevisionStage"
        type="button"
        class="btn btn--primary workspace__submit-btn"
        [disabled]="!product.showRevisionSubmission"
        (click)="openSubmitForReview(product)"
        [attr.data-testid]="'workspace-product-' + product.testId + '-submit-revision'"
      >
        {{ product.showRevisionSubmission ? "Submit Revision" : "Revision Document Required" }}
      </button>

      <div
        *ngIf="product.isPendingState"
        class="workspace__success"
        [attr.data-testid]="'workspace-product-' + product.testId + '-pending'"
      >
        <span class="material-icons">check_circle</span> {{ product.pendingLabel }}
      </div>

      <div
        *ngIf="product.isComplete"
        class="workspace__success"
        [attr.data-testid]="'workspace-product-' + product.testId + '-complete'"
      >
        <span class="material-icons">check_circle</span> Order Complete
      </div>
    </div>

    <!-- =================== PER-PRODUCT CARD SLOTS =================== -->
    <div class="workspace__slots">
      <section class="workspace__slot">
        <p class="workspace__subcard-label">Documents</p>
        <app-documents-card
          [siteDetail]="product.siteDetail"
          [documents]="product.documents"
          [isAlta]="product.isAlta"
          scope="product"
          [testIdBase]="product.testId"
          [readOnly]="readOnly"
          (update)="onProductUpdate($event)"
        ></app-documents-card>
      </section>

      <section class="workspace__slot">
        <p class="workspace__subcard-label">Comments</p>
        <app-comments-card
          [siteDetail]="product.siteDetail"
          [readOnly]="readOnly"
          (update)="onProductUpdate($event)"
        ></app-comments-card>
      </section>
    </div>
  </section>
</ng-template>

<!-- Reused confirmation dialog (post-award only), scoped to the product whose
     deliverable submit is in flight (see openSubmitForReview). -->
<submit-for-review-dialog
  [active]="submitForReviewActive"
  [documents]="dialogDocuments"
  [siteType]="dialogSiteType"
  [siteSubType]="dialogSiteSubType"
  [address]="address"
  [status]="dialogStatus"
  [isAlta]="dialogIsAlta"
  (modalClosed)="toggleSubmitForReviewModal()"
  (submitReview)="submitReview()"
  (submitRevision)="submitRevision()"
  [ngClass]="{ 'documents-dialog--active': submitForReviewActive }"
/>
